<template>
  <div class="relative flex h-[calc(100%-2.75rem)] flex-col">
    <div class="min-h-0 flex-1 overflow-y-auto p-4 pb-32">
      <div class="space-y-5">
        <div>
          <label class="text-lgmx_gray-900 mb-1 block text-[13px] font-medium">
            Name
          </label>
          <input
            v-model="name"
            type="text"
            class="form-input border-lgmx_gray-300 text-lms_black-300 w-full rounded-md border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Enter overlay name"
          />
        </div>

        <div>
          <label class="text-lgmx_gray-900 mb-1 block text-[13px] font-medium">
            Image
          </label>
          <div
            v-if="!imagePreviewUrl"
            class="border-lgmx_gray-300 flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed p-6 transition-colors hover:border-blue-400 hover:bg-blue-50/50"
            @click="openFilePicker"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
            :class="{ 'border-blue-400 bg-blue-50/50': isDragging }"
          >
            <svg
              class="text-lgmx_gray-700 mb-2 size-8"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span class="text-lgmx_gray-900 text-sm">
              Click or drag image here
            </span>
            <span class="text-lgmx_gray-700 mt-1 text-xs">
              PNG or JPG, max 10MB
            </span>
          </div>
          <div v-else class="space-y-2">
            <div class="relative overflow-hidden rounded-md border">
              <img
                :src="imagePreviewUrl"
                class="h-32 w-full bg-gray-50 object-contain"
                alt="Overlay preview"
              />
              <button
                type="button"
                class="absolute right-1 top-1 rounded-full bg-white/90 p-1 shadow hover:bg-white"
                @click="removeImage"
              >
                <svg
                  class="size-4 text-gray-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <span class="text-lgmx_gray-700 text-xs">{{ fileName }}</span>
          </div>
          <input
            ref="fileInput"
            type="file"
            accept="image/png,image/jpeg"
            class="hidden"
            @change="handleFileSelect"
          />
          <p v-if="fileError" class="mt-1 text-xs text-red-600">
            {{ fileError }}
          </p>
        </div>

        <div>
          <label class="text-lgmx_gray-900 mb-1 block text-[13px] font-medium">
            Opacity: {{ Math.round(opacity * 100) }}%
          </label>
          <input
            v-model.number="opacity"
            type="range"
            min="0"
            max="1"
            step="0.05"
            class="w-full accent-blue-500"
          />
        </div>
      </div>
    </div>
    <CommonBottomButtonSection>
      <div class="flex gap-2">
        <Button
          size="sm"
          variant="destructive"
          class="w-full"
          @click="cancel"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          class="w-full"
          :disabled="!canSave"
          :is-loading="isSaving"
          is-loading-text="Saving..."
          @click="save"
        >
          Save
        </Button>
      </div>
    </CommonBottomButtonSection>
  </div>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';
import { Button } from '@gpx-spa/ui';
import CommonBottomButtonSection from '@/components/_common/CommonBottomButtonSection.vue';
import { hasValidCorners } from '@/utils/map/overlayCorners';

const props = defineProps({
  overlay: {
    type: Object,
    default: null,
  },
  corners: {
    type: Object,
    default: null,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'save',
  'cancel',
  'update:imageUrl',
  'update:opacity',
]);

const name = ref(props.overlay?.name || '');
const opacity = ref(props.overlay?.opacity ?? 1);
const file = ref(null);
const imagePreviewUrl = ref(props.overlay?.file?.url || null);
const fileName = ref(props.overlay?.file?.original_name || '');
const fileInput = ref(null);
const isDragging = ref(false);
const fileError = ref('');

const canSave = computed(() => {
  return name.value.trim() && imagePreviewUrl.value && hasValidCorners(props.corners);
});

function openFilePicker() {
  fileInput.value?.click();
}

function handleFileSelect(event) {
  const selected = event.target.files?.[0];
  if (selected) processFile(selected);
}

function handleDrop(event) {
  isDragging.value = false;
  const dropped = event.dataTransfer.files?.[0];
  if (dropped) processFile(dropped);
}

function processFile(f) {
  if (!f.type.match(/^image\/(png|jpe?g)$/)) {
    fileError.value = 'File must be PNG or JPEG';
    return;
  }
  if (f.size > 10 * 1024 * 1024) {
    fileError.value = 'File must be under 10MB';
    return;
  }

  fileError.value = '';
  const oldUrl = imagePreviewUrl.value;
  const hadFile = file.value;

  file.value = f;
  fileName.value = f.name;

  if (oldUrl && hadFile) {
    URL.revokeObjectURL(oldUrl);
  }
  imagePreviewUrl.value = URL.createObjectURL(f);
  emit('update:imageUrl', imagePreviewUrl.value);
}

function removeImage() {
  if (imagePreviewUrl.value && file.value) {
    URL.revokeObjectURL(imagePreviewUrl.value);
  }
  file.value = null;
  imagePreviewUrl.value = null;
  fileName.value = '';
  emit('update:imageUrl', null);
  if (fileInput.value) fileInput.value.value = '';
}

function save() {
  emit('save', {
    name: name.value.trim(),
    opacity: opacity.value,
    file: file.value,
    corners: props.corners,
  });
}

function cancel() {
  emit('cancel');
}

watch(opacity, (val) => {
  emit('update:opacity', val);
});

onUnmounted(() => {
  if (imagePreviewUrl.value && file.value) {
    URL.revokeObjectURL(imagePreviewUrl.value);
  }
});
</script>
